<template>
  <v-container fluid class="pa-6">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-shield-crown" class="mr-2" />
          <span>Admin Әрекеттер Тарихы</span>
        </div>
        <div class="d-flex gap-2 align-center">
          <v-select
            v-model="selectedAdmin"
            :items="adminUsers"
            item-title="fio"
            item-value="id"
            label="Admin таңдау"
            variant="outlined"
            density="compact"
            clearable
            class="admin-select"
            style="min-width: 200px;"
          />
          <v-btn
            color="primary"
            prepend-icon="mdi-refresh"
            @click="loadAdminActions"
            :loading="loading"
          >
            Жаңарту
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Statistics Cards -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card color="purple" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="48" class="mb-2">mdi-chart-line</v-icon>
                <div class="text-h5">{{ stats.totalActions || 0 }}</div>
                <div class="text-body-2">Жалпы әрекеттер</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card color="success" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="48" class="mb-2">mdi-test-tube</v-icon>
                <div class="text-h5">{{ stats.testActions || 0 }}</div>
                <div class="text-body-2">Тест әрекеттері</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card color="info" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="48" class="mb-2">mdi-video-box</v-icon>
                <div class="text-h5">{{ stats.lessonActions || 0 }}</div>
                <div class="text-body-2">Сабақ әрекеттері</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card color="warning" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="48" class="mb-2">mdi-account-check</v-icon>
                <div class="text-h5">{{ stats.userActions || 0 }}</div>
                <div class="text-body-2">Қолданушы әрекеттері</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Actions List -->
        <v-card variant="outlined">
          <v-card-title class="text-h6">
            <v-icon class="mr-2">mdi-history</v-icon>
            Соңғы әрекеттер
            <v-spacer />
            <v-chip
              v-if="selectedAdmin"
              color="primary"
              closable
              @click:close="selectedAdmin = null"
            >
              {{ getAdminName(selectedAdmin) }}
            </v-chip>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="actions"
            :loading="loading"
            :items-per-page="25"
            class="elevation-0"
            no-data-text="Әрекеттер табылмады"
            loading-text="Жүктелуде..."
          >
            <!-- Action column -->
            <template #item.action="{ item }">
              <v-chip
                :color="getActionColor(item.action)"
                size="small"
                variant="tonal"
              >
                {{ getActionText(item.action) }}
              </v-chip>
            </template>

            <!-- Target Type column -->
            <template #item.targetType="{ item }">
              <div class="d-flex align-center">
                <v-icon :icon="getTargetIcon(item.targetType)" size="small" class="mr-1" />
                {{ getTargetText(item.targetType) }}
              </div>
            </template>

            <!-- Description column -->
            <template #item.description="{ item }">
              <div class="description-cell">
                {{ item.description }}
                <v-tooltip v-if="item.details" location="top">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="small" class="ml-1" color="info">mdi-information</v-icon>
                  </template>
                  <div class="text-caption">
                    <pre>{{ JSON.stringify(item.details, null, 2) }}</pre>
                  </div>
                </v-tooltip>
              </div>
            </template>

            <!-- Admin column -->
            <template #item.adminName="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="mr-2">
                  <v-icon>mdi-account-tie</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-medium">{{ item.adminName }}</div>
                  <div class="text-caption text-medium-emphasis">{{ item.adminRole }}</div>
                </div>
              </div>
            </template>

            <!-- Timestamp column -->
            <template #item.timestamp="{ item }">
              <div class="text-body-2">
                {{ formatDateTime(item.timestamp) }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ formatTimeAgo(item.timestamp) }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import { useSnackbarStore } from '@/stores/snackbar'

const { t } = useI18n()
const snackbarStore = useSnackbarStore()

// State
const loading = ref(false)
const actions = ref([])
const adminUsers = ref([])
const selectedAdmin = ref(null)
const stats = ref({
  totalActions: 0,
  testActions: 0,
  lessonActions: 0,
  userActions: 0
})

// Table headers
const headers = [
  { title: 'Әрекет', key: 'action', sortable: true },
  { title: 'Мақсат', key: 'targetType', sortable: true },
  { title: 'Сипаттама', key: 'description', sortable: false },
  { title: 'Admin', key: 'adminName', sortable: true },
  { title: 'Уақыт', key: 'timestamp', sortable: true }
]

// Computed
const totalActions = computed(() => actions.value.length)

// Methods
const loadAdminActions = async () => {
  loading.value = true
  try {
    const params = selectedAdmin.value ? { adminId: selectedAdmin.value } : {}
    const response = await api.get('/admin/admin-actions', { params })
    
    actions.value = response.data.actions || []
    
    // Calculate statistics
    const actionsByType = actions.value.reduce((acc, action) => {
      const category = getActionCategory(action.action)
      acc[category] = (acc[category] || 0) + 1
      return acc
    }, {})
    
    stats.value = {
      totalActions: actions.value.length,
      testActions: actionsByType.test || 0,
      lessonActions: actionsByType.lesson || 0,
      userActions: actionsByType.user || 0
    }
    
  } catch (error) {
    snackbarStore.error('Admin әрекеттерін жүктеуде қате орын алды')
    console.error('Failed to load admin actions:', error)
  } finally {
    loading.value = false
  }
}

const loadAdminUsers = async () => {
  try {
    const response = await api.get('/admin/users')
    adminUsers.value = response.data.users
      .filter(user => user.role === 'ADMIN' || user.role === 'ADMIN_LIMITED')
      .map(user => ({
        id: user.id,
        fio: user.fio,
        role: user.role
      }))
  } catch (error) {
    console.error('Failed to load admin users:', error)
  }
}

const getActionColor = (action) => {
  if (action.includes('CREATE')) return 'success'
  if (action.includes('UPDATE')) return 'info'
  if (action.includes('DELETE')) return 'error'
  if (action.includes('APPROVE')) return 'success'
  if (action.includes('REJECT')) return 'error'
  return 'primary'
}

const getActionText = (action) => {
  const actionMap = {
    CREATE_TEST: 'Тест жасау',
    UPDATE_TEST: 'Тест өзгерту',
    DELETE_TEST: 'Тест жою',
    BULK_DELETE_TESTS: 'Тестерді жаппай жою',
    CREATE_LESSON: 'Сабақ жасау',
    UPDATE_LESSON: 'Сабақ өзгерту',
    DELETE_LESSON: 'Сабақ жою',
    CREATE_LESSON_CATEGORY: 'Категория жасау',
    UPDATE_LESSON_CATEGORY: 'Категория өзгерту',
    DELETE_LESSON_CATEGORY: 'Категория жою',
    APPROVE_USER: 'Қолданушыны растау',
    REJECT_USER: 'Қолданушыны қабылдамау',
    CREATE_USER: 'Қолданушы жасау',
    UPDATE_USER: 'Қолданушы өзгерту',
    DELETE_USER: 'Қолданушы жою'
  }
  return actionMap[action] || action
}

const getActionCategory = (action) => {
  if (action.includes('TEST')) return 'test'
  if (action.includes('LESSON')) return 'lesson'
  if (action.includes('USER')) return 'user'
  return 'other'
}

const getTargetIcon = (targetType) => {
  const iconMap = {
    TEST: 'mdi-test-tube',
    LESSON: 'mdi-video-box',
    LESSON_CATEGORY: 'mdi-folder-video',
    USER: 'mdi-account',
    INSTRUCTOR: 'mdi-account-tie',
    SYSTEM: 'mdi-cog'
  }
  return iconMap[targetType] || 'mdi-help-circle'
}

const getTargetText = (targetType) => {
  const targetMap = {
    TEST: 'Тест',
    LESSON: 'Сабақ',
    LESSON_CATEGORY: 'Категория',
    USER: 'Қолданушы',
    INSTRUCTOR: 'Инструктор',
    SYSTEM: 'Жүйе'
  }
  return targetMap[targetType] || targetType
}

const getAdminName = (adminId) => {
  const admin = adminUsers.value.find(a => a.id === adminId)
  return admin ? admin.fio : 'Белгісіз'
}

const formatDateTime = (timestamp) => {
  const date = new Date(timestamp)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatTimeAgo = (timestamp) => {
  const now = new Date()
  const diff = now - new Date(timestamp)
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 1) return 'дәл қазір'
  if (minutes < 60) return `${minutes} минут бұрын`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} сағат бұрын`
  
  const days = Math.floor(hours / 24)
  return `${days} күн бұрын`
}

// Watch for admin selection changes
watch(selectedAdmin, () => {
  loadAdminActions()
})

// Lifecycle
onMounted(async () => {
  await loadAdminUsers()
  await loadAdminActions()
})
</script>

<style scoped>
.admin-select {
  max-width: 250px;
}

.description-cell {
  max-width: 300px;
  word-wrap: break-word;
}

.stat-card {
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}
</style>